/* globals document OffscreenCanvas HTMLCanvasElement */
import { WHITESPACE } from './constants.ts';
import { fontStringParser } from './fontStringParser.ts';
import { fontToString } from './fontToString.ts';
import type { Token } from './parser/tokens.ts';
import type { MinimalCanvas, FontProps, MeasureOptions } from './types.ts';

const defaultFont = fontStringParser('12px/14px sans-serif');

const defaultOptions = {
  trim: true,
  collapse: true
};

const wCache = {};

type MeasureFn = (text: string, font: string | FontProps) => number;

// This is just guesswork but works surprisingly well.
// Intended to be used to renderer for tests, or as a last
// resort in a server env.
export function getDumbHandler (): MeasureFn {
  return (text: string, font: string | FontProps) => {
    const f = typeof font === 'string' ? fontStringParser(font) : font;
    let size = f.size ?? 12;
    if (f.family && /\bmonospace\b/.test(f.family)) {
      size *= 0.6;
    }
    else {
      size *= 0.45;
      if (f.weight && f.weight > 400) {
        size *= 1.18;
      }
    }
    return text.length * size;
  };
}

function getMeasureFromCanvas (canvas?: MinimalCanvas | null): MeasureFn | void {
  if (canvas && canvas.getContext) {
    const context = canvas.getContext('2d');
    if (context && typeof context.measureText === 'function') {
      return (text, font) => {
        context.font = String(font);
        return context.measureText(text).width;
      };
    }
  }
}

export function getBrowserCanvas () {
  const doc = typeof document !== 'undefined' ? document : null;
  return (
    (typeof OffscreenCanvas !== 'undefined' && new OffscreenCanvas(100, 100)) ||
    (doc && doc.createElement && doc.createElement('canvas')) ||
    null
  );
}

let measure: MeasureFn = getMeasureFromCanvas(getBrowserCanvas()) || getDumbHandler();
/**
 * Set the canvas to use when measuring text. This will be needed when using {@link measureText}
 * in a non-browser environment.
 * @param canvas The canvas interface to use.
 */
export function setMeasureCanvas (canvas: MinimalCanvas | null) {
  if (canvas == null) {
    measure = getMeasureFromCanvas(getBrowserCanvas()) || getDumbHandler();
  }
  else if (canvas) {
    measure = getMeasureFromCanvas(canvas) || getDumbHandler();
  }
  else {
    throw new Error('setMeasureCanvas argument is not a canvas or null');
  }
}

/**
 * Measure a string of text as printed with a specified font and return its width.
 *
 * Be careful that in non-browser environments you may want to supply an alternative
 * canvas using {@link setMeasureCanvas} or this method will default to a crude
 * "best guess" method.
 *
 * @param token A string or token of text to measure.
 * @param font A CSS font shorthand string or a collection of font properties.
 * @param options Text handling options.
 */
export function measureText (
  token: string | Token,
  font: string | FontProps,
  options: MeasureOptions = defaultOptions
): number {
  if (typeof font === 'string') {
    font = fontStringParser(font);
  }
  else {
    font = { ...defaultFont, ...font };
  }

  const opts = Object.assign({}, defaultOptions, options);
  let s = String(token);
  // empty
  if (!s) {
    return 0;
  }
  // whitespace
  if (s in WHITESPACE) {
    const cacheId = fontToString(font, true) + '/' + s;
    if (!(cacheId in wCache)) {
      wCache[cacheId] = measure(`_${s}_`, fontToString(font)) - measure('__', fontToString(font));
    }
    return wCache[cacheId];
  }
  // When there are line breaks in the string but we're either not trimming
  // whitespace or not collapsing whitespace, do what the input element does
  // and convert all "\n" to " ".
  if (!opts.trim || !opts.collapse) {
    s = s.replace(/\n/g, ' ');
  }
  else if (opts.trim) {
    s = s.replace(/\n/g, ' ').trim();
  }
  else if (opts.collapse) {
    s = s.replace(/\s+/g, ' ');
  }
  const tracking = typeof token === 'string' ? 0 : token.font.tracking || 0;
  return measure(s, fontToString(font)) + (font.size ?? 12) * tracking;
}
